import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, Alert, ScrollView, ActivityIndicator } from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import AppShell from '../components/layout/AppShell';
import PersonalProfile from '../components/profile/PersonalProfile';
import BusinessDetails from '../components/profile/BusinessDetails';
import EditProfileModal from '../components/profile/EditProfileModal';
import EditBusinessModal from '../components/profile/EditBusinessModal';
import ProfileHeader from '../components/profile/ProfileHeader';
import BusinessCard from '../components/dashboard/BusinessCard';
import ConnectionTypeModal from '../components/network/ConnectionTypeModal';
import { RootStackParamList } from '../types/navigation';
import { PersonalDetailsFormData, BusinessFormData, UserProfile } from '../types/profile';
import type { User } from '../types/auth';
import type { ConnectionType } from '../types/network';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { updateProfile, getUserProfileById } from '../lib/api/users';
import { storage } from '../utils/storage';
import { showSuccessToast } from '../utils/toast.native';
import {
  getUserProfile,
  getBusinessProfile,
  updateUserProfile,
  createBusiness,
  updateBusiness,
  deleteBusiness,
} from '../data/mockProfileData';
import { getConnectionStatus as getMockConnectionStatus, sendConnectionRequest } from '../data/mockNetworkData';
import { getConnectionStatus as getApiConnectionStatus, sendConnectionRequest as sendApiConnectionRequest } from '../lib/api/network';
import ThemedText from '../components/common/ThemedText';

type ProfileScreenRouteProp = RouteProp<RootStackParamList, 'Profile'>;
type ProfileScreenNavigationProp = StackNavigationProp<RootStackParamList>;
type ProfileData = UserProfile & Partial<User>;

const ProfileScreen: React.FC = () => {
  const route = useRoute<ProfileScreenRouteProp>();
  const navigation = useNavigation<ProfileScreenNavigationProp>();
  const { theme } = useTheme();
  const { user, reloadUser } = useAuth();
  const insets = useSafeAreaInsets();
  const targetUserId = route.params?.userId;
  
  // If no userId provided, show own profile
  const userId = targetUserId || user?.userId;
  const isOwnProfile = !targetUserId || targetUserId === user?.userId;

  const [refreshKey, setRefreshKey] = useState(0);
  const [editProfileVisible, setEditProfileVisible] = useState(false);
  const [editBusinessVisible, setEditBusinessVisible] = useState(false);
  const [showPersonalDetails, setShowPersonalDetails] = useState(false);
  const [showBusinessDetails, setShowBusinessDetails] = useState(false);
  
  // Loading state for fetching profile
  const [isLoadingProfile, setIsLoadingProfile] = useState(!isOwnProfile);
  const [fetchedProfile, setFetchedProfile] = useState<any>(null);
  
  // Connection status state
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'pending' | 'none' | undefined>(undefined);
  
  // Connection type modal state
  const [showConnectionTypeModal, setShowConnectionTypeModal] = useState(false);
  const [pendingConnectionUserId, setPendingConnectionUserId] = useState<string | null>(null);
  
  // Personal profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Business details edit state
  const [isEditingBusiness, setIsEditingBusiness] = useState(false);
  const [isSavingBusiness, setIsSavingBusiness] = useState(false);

  // Fetch profile data when viewing someone else's profile
  useEffect(() => {
    if (!isOwnProfile && userId) {
      fetchUserProfile();
      fetchConnectionStatus();
    }
  }, [userId, isOwnProfile, refreshKey]);

  // Listen for refresh parameter from navigation
  useEffect(() => {
    if (route.params?.refresh) {
      setRefreshKey(prev => prev + 1);
      // Clear the refresh param to avoid repeated refreshes
      navigation.setParams({ refresh: undefined } as any);
    }
  }, [route.params?.refresh]);

  // Listen for showBusiness parameter to automatically open business details
  useEffect(() => {
    if (route.params?.showBusiness) {
      setShowBusinessDetails(true);
      // Clear the param
      navigation.setParams({ showBusiness: undefined } as any);
    }
  }, [route.params?.showBusiness]);

  const fetchUserProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login again');
        navigation.goBack();
        return;
      }

      const response = await getUserProfileById(token, userId!);
      if (response.success && response.user) {
        setFetchedProfile(response.user);
      } else {
        Alert.alert('Error', 'Profile not found');
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      Alert.alert('Error', error.message || 'Failed to load profile');
      navigation.goBack();
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const token = await storage.getToken();
      if (!token || !userId) return;

      // For mock users (starting with 'user-'), use mock data
      if (userId.startsWith('user-')) {
        const mockCurrentUserId = user?.userId || 'user-1';
        const status = getMockConnectionStatus(mockCurrentUserId, userId);
        setConnectionStatus(status);
      } else {
        // For real users, fetch from API
        const response = await getApiConnectionStatus(token, userId);
        setConnectionStatus(response.status);
      }
    } catch (error: any) {
      console.error('Error fetching connection status:', error);
      // Default to 'none' on error
      setConnectionStatus('none');
    }
  };

  // Use real user data for own profile, fetched data for others
  const profile: ProfileData | undefined = isOwnProfile && user
    ? {
        userId: user.userId,
        name: user.name || '',
        profession: user.profession || '',
        designation: user.designation,
        industry: user.industry || '',
        city: user.city || '',
        phoneNumber: user.phoneNumber,
        photo: user.photo,
        email: user.email,
        previewImageUrl: user.previewImageUrl,
        // Contact fields
        dateOfBirth: user.dateOfBirth,
        company: user.company,
        gender: user.gender,
        country: user.country,
        state: user.state,
        // Personal fields
        spouse: user.spouse,
        children: user.children,
        animals: user.animals,
        hobbies: user.hobbies,
        activityOfInterest: user.activityOfInterest,
        cityOfResidence: user.cityOfResidence,
        burningDesire: user.burningDesire,
        somethingNoOneKnows: user.somethingNoOneKnows,
        keyToSuccesses: user.keyToSuccesses,
        personnelGoals: user.personnelGoals,
        personnelAccomplishments: user.personnelAccomplishments,
        personnelInterests: user.personnelInterests,
        personnelNetwork: user.personnelNetwork,
        personnelSkills: user.personnelSkills,
        // Business fields
        businessName: user.businessName || user.businessTitle,
        businessLocation: user.businessLocation,
        previousTypeOfJobs: user.previousTypeOfJobs,
        businessGoals: user.businessGoals,
        businessAccomplishments: user.businessAccomplishments,
        businessInterests: user.businessInterests,
        businessNetwork: user.businessNetwork,
        businessSkills: user.businessSkills,
        // Business Details fields
        businessIndustry: user.businessIndustry,
        businessPhoto: user.businessPhoto,
        businessTitle: user.businessTitle,
        businessType: user.businessType,
        businessCountry: user.businessCountry,
        businessPlace: user.businessPlace,
        businessLocationFull: user.businessLocationFull,
        businessWebsite: user.businessWebsite,
        businessLink: user.businessLink,
        businessMobileNumber: user.businessMobileNumber,
        productsServices: user.productsServices,
        noOfEmployees: user.noOfEmployees,
        linkedinId: user.linkedinId,
        companyEmail: user.companyEmail,
        shareValue: user.shareValue,
        aboutCompany: user.aboutCompany,
      }
    : fetchedProfile
    ? {
        userId: fetchedProfile.id,
        name: fetchedProfile.name || '',
        profession: fetchedProfile.profession || '',
        designation: fetchedProfile.designation,
        industry: fetchedProfile.industry || '',
        city: fetchedProfile.city || '',
        phoneNumber: fetchedProfile.phone,
        photo: fetchedProfile.photo,
        email: fetchedProfile.email,
        previewImageUrl: fetchedProfile.previewImageUrl,
        // Contact fields
        dateOfBirth: fetchedProfile.dateOfBirth,
        company: fetchedProfile.company,
        gender: fetchedProfile.gender,
        country: fetchedProfile.country,
        state: fetchedProfile.state,
        // Personal fields
        spouse: fetchedProfile.spouse,
        children: fetchedProfile.children,
        animals: fetchedProfile.animals,
        hobbies: fetchedProfile.hobbies,
        activityOfInterest: fetchedProfile.activityOfInterest,
        cityOfResidence: fetchedProfile.cityOfResidence,
        burningDesire: fetchedProfile.burningDesire,
        somethingNoOneKnows: fetchedProfile.somethingNoOneKnows,
        keyToSuccesses: fetchedProfile.keyToSuccesses,
        personnelGoals: fetchedProfile.personnelGoals,
        personnelAccomplishments: fetchedProfile.personnelAccomplishments,
        personnelInterests: fetchedProfile.personnelInterests,
        personnelNetwork: fetchedProfile.personnelNetwork,
        personnelSkills: fetchedProfile.personnelSkills,
        // Business fields
        businessName: fetchedProfile.businessName || fetchedProfile.businessTitle,
        businessLocation: fetchedProfile.businessLocation,
        previousTypeOfJobs: fetchedProfile.previousTypeOfJobs,
        businessGoals: fetchedProfile.businessGoals,
        businessAccomplishments: fetchedProfile.businessAccomplishments,
        businessInterests: fetchedProfile.businessInterests,
        businessNetwork: fetchedProfile.businessNetwork,
        businessSkills: fetchedProfile.businessSkills,
        // Business Details fields
        businessIndustry: fetchedProfile.businessIndustry,
        businessPhoto: fetchedProfile.businessPhoto,
        businessTitle: fetchedProfile.businessTitle,
        businessType: fetchedProfile.businessType,
        businessCountry: fetchedProfile.businessCountry,
        businessPlace: fetchedProfile.businessPlace,
        businessLocationFull: fetchedProfile.businessLocationFull,
        businessWebsite: fetchedProfile.businessWebsite,
        businessLink: fetchedProfile.businessLink,
        businessMobileNumber: fetchedProfile.businessMobileNumber,
        productsServices: fetchedProfile.productsServices,
        noOfEmployees: fetchedProfile.noOfEmployees,
        linkedinId: fetchedProfile.linkedinId,
        companyEmail: fetchedProfile.companyEmail,
        shareValue: fetchedProfile.shareValue,
        aboutCompany: fetchedProfile.aboutCompany,
      }
    : undefined;

  // Show loading state while fetching profile
  if (isLoadingProfile) {
    return (
      <AppShell title="Profile">
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
          <ThemedText style={[styles.loadingText, { color: theme.textSecondary }]}>
            Loading profile...
          </ThemedText>
        </View>
      </AppShell>
    );
  }

  if (!profile) {
    return (
      <AppShell title="Profile">
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={48} color={theme.textTertiary} />
          <ThemedText style={[styles.errorText, { color: theme.textTertiary }]}>Profile not found</ThemedText>
        </View>
      </AppShell>
    );
  }

  const business = profile.businessId ? getBusinessProfile(profile.businessId) : undefined;
  
  const personalIndustry = profile.industry;
  const businessIndustryLabel = business?.category || profile.businessIndustry || profile.businessTitle;
  const businessName = business?.name || profile.businessName || profile.businessTitle;
  const businessLogo = profile.businessPhoto || business?.logoUrl;
  // Check if user has any business information
  const hasBusinessProfile = Boolean(
    businessName || 
    profile.businessTitle || 
    profile.businessIndustry || 
    profile.companyEmail || 
    profile.businessPhoto
  );
  
  // Debug logs
  console.log('🔗 [ProfileScreen] Connection Status Debug:');
  console.log('🔗 [ProfileScreen] Current user ID:', user?.userId);
  console.log('🔗 [ProfileScreen] Target user ID:', userId);
  console.log('🔗 [ProfileScreen] Target starts with user-:', targetUserId?.startsWith('user-'));
  console.log('🔗 [ProfileScreen] Is own profile:', isOwnProfile);
  console.log('🔗 [ProfileScreen] Connection status:', connectionStatus);
  console.log('🏢 [ProfileScreen] businessId from profile:', profile.businessId);
  console.log('🏢 [ProfileScreen] mock business lookup result:', business);
  console.log('🏢 [ProfileScreen] businessName resolved:', businessName);
  console.log('🏢 [ProfileScreen] businessIndustry label:', businessIndustryLabel);
  console.log('🏢 [ProfileScreen] businessLogo url:', businessLogo);
  console.log('🏢 [ProfileScreen] hasBusinessProfile flag:', hasBusinessProfile);
  const contactDetails = [
    { icon: 'location-outline', value: profile.city },
    { icon: 'call-outline', value: profile.phoneNumber || 'Phone not provided' },
  ];

  // Debug: Log profile data
  console.log('🖼️ [ProfileScreen] Is own profile:', isOwnProfile);
  console.log('🖼️ [ProfileScreen] Profile photo:', profile.photo);
  console.log('🖼️ [ProfileScreen] Profile email:', profile.email);
  console.log('🖼️ [ProfileScreen] Profile phone:', profile.phoneNumber);

  const handleMessage = () => {
    navigation.navigate('PersonalChat', { 
      userId: userId!, 
      userName: profile?.name || 'User'
    });
  };

  const handleConnect = async () => {
    if (!userId) return;
    
    // Show connection type modal instead of immediately sending
    setPendingConnectionUserId(userId);
    setShowConnectionTypeModal(true);
  };

  const handleConnectionTypeSelect = async (connectionType: ConnectionType) => {
    if (!pendingConnectionUserId) return;
    
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login again');
        return;
      }

      // For mock users, use mock function
      if (pendingConnectionUserId.startsWith('user-')) {
        const mockCurrentUserId = user?.userId || 'user-1';
        sendConnectionRequest(mockCurrentUserId, pendingConnectionUserId, connectionType);
      } else {
        // For real users, use API
        await sendApiConnectionRequest(token, pendingConnectionUserId, connectionType);
      }
      
      // Refresh connection status
      await fetchConnectionStatus();
      showSuccessToast(`Connection request sent to ${profile?.name}`);
    } catch (error: any) {
      console.error('Error sending connection request:', error);
      Alert.alert('Error', error.message || 'Failed to send connection request');
    } finally {
      setShowConnectionTypeModal(false);
      setPendingConnectionUserId(null);
    }
  };

  const handleConnectionTypeCancel = () => {
    setShowConnectionTypeModal(false);
    setPendingConnectionUserId(null);
  };

  const handleWriteReview = () => {
    if (!userId || !profile?.name) return;
    navigation.navigate('SubmitReview', {
      userId: userId,
      userName: profile.businessName || profile.name
    });
  };

  const handleSaveProfile = (data: PersonalDetailsFormData) => {
    if (!userId) return;
    updateUserProfile(userId, data);
    setRefreshKey((prev) => prev + 1);
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
  };

  const handleSaveProfileChanges = async (data: any) => {
    setIsSavingProfile(true);
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login again');
        return;
      }

      await updateProfile(token, data);
      
      // Reload user data from backend to get updated fields
      await reloadUser();
      
      setIsEditingProfile(false);
      showSuccessToast('Profile updated successfully');
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to update profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancelProfileEdit = () => {
    setIsEditingProfile(false);
    Alert.alert('Changes Discarded', 'Your changes have been discarded');
  };

  const handleEditBusiness = () => {
    setIsEditingBusiness(true);
  };

  const handleSaveBusinessChanges = async (data: any) => {
    setIsSavingBusiness(true);
    try {
      const token = await storage.getToken();
      if (!token) {
        Alert.alert('Error', 'Please login again');
        return;
      }

      await updateProfile(token, data);
      
      // Reload user data from backend to get updated fields
      await reloadUser();
      
      setIsEditingBusiness(false);
      showSuccessToast('Business profile updated successfully');
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to update business profile');
    } finally {
      setIsSavingBusiness(false);
    }
  };

  const handleCancelBusinessEdit = () => {
    setIsEditingBusiness(false);
    Alert.alert('Changes Discarded', 'Your changes have been discarded');
  };

  const handleDeleteBusinessDetails = () => {
    Alert.alert(
      'Delete Business Profile',
      'Are you sure you want to delete all your business profile? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setIsSavingBusiness(true);
            try {
              const token = await storage.getToken();
              if (!token) {
                Alert.alert('Error', 'Please login again');
                return;
              }

              // Clear all business fields
              await updateProfile(token, {
                businessPhoto: '',
                businessTitle: '',
                businessIndustry: '',
                businessType: '',
                businessCountry: '',
                businessPlace: '',
                productsServices: '',
                noOfEmployees: '',
                businessMobileNumber: '',
                linkedinId: '',
                companyEmail: '',
                businessWebsite: '',
                shareValue: '',
                businessLink: '',
                businessLocationFull: '',
                aboutCompany: '',
              });
              
              await reloadUser();
              setIsEditingBusiness(false);
              showSuccessToast('Business profile deleted successfully');
            } catch (error: any) {
              Alert.alert('Error', error.message || 'Failed to delete business profile');
            } finally {
              setIsSavingBusiness(false);
            }
          },
        },
      ]
    );
  };

  const handleCreateBusiness = (data: BusinessFormData) => {
    if (!userId) return;
    createBusiness(userId, data);
    setRefreshKey((prev) => prev + 1);
  };

  const handleUpdateBusiness = (data: BusinessFormData) => {
    if (profile.businessId) {
      updateBusiness(profile.businessId, data);
      setRefreshKey((prev) => prev + 1);
    }
  };

  const handleDeleteBusiness = () => {
    if (profile.businessId && userId) {
      deleteBusiness(profile.businessId, userId);
      setRefreshKey((prev) => prev + 1);
    }
  };

  const handleSettings = () => {
    navigation.navigate('Settings');
  };

  const renderConnectionButton = () => {
    if (isOwnProfile) return null;

    if (connectionStatus === 'connected') {
      // Don't render anything - connected users only see message button
      return null;
    }

    if (connectionStatus === 'pending') {
      return (
        <View
          style={[styles.actionButton, { backgroundColor: theme.backgroundSecondary, borderColor: '#F59E0B' }]}
        >
          <Ionicons name="time-outline" size={18} color="#F59E0B" />
          <ThemedText style={[styles.actionButtonText, { color: '#F59E0B' }]}>Pending</ThemedText>
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={[styles.actionButton, { backgroundColor: theme.primary }]}
        onPress={handleConnect}
        activeOpacity={0.8}
      >
        <Ionicons name="person-add-outline" size={18} color="#000000" />
        <ThemedText style={[styles.actionButtonText, { color: '#000000' }]}>Connect</ThemedText>
      </TouchableOpacity>
    );
  };

  if (showPersonalDetails) {
    const editIcon = isOwnProfile && !isEditingProfile ? (
      <TouchableOpacity
        style={{ marginRight: 16 }}
        onPress={handleEditProfile}
        activeOpacity={0.7}
      >
        <Ionicons name="create-outline" size={26} color={theme.text} />
      </TouchableOpacity>
    ) : null;

    const headerRight = isEditingProfile
      ? <View style={{ width: 44, height: 44 }} />
      : editIcon;

    return (
      <AppShell 
        title="Personal Profile" 
        showBackButton={true} 
        onBackPress={() => {
          setShowPersonalDetails(false);
          setIsEditingProfile(false); // Reset edit mode when going back
        }}
        rightActions={headerRight}
        scrollable={false}
      >
        <View style={{ flex: 1 }}>
          <PersonalProfile
            profile={profile}
            isOwnProfile={isOwnProfile}
            onEdit={isOwnProfile ? () => setEditProfileVisible(true) : undefined}
            isEditing={isEditingProfile}
            onEditPress={handleEditProfile}
            onSavePress={handleSaveProfileChanges}
            onCancelPress={handleCancelProfileEdit}
            isSaving={isSavingProfile}
          />
        </View>
      </AppShell>
    );
  }

  if (showBusinessDetails) {
    const editIcon = isOwnProfile && !isEditingBusiness ? (
      <TouchableOpacity
        style={{ marginRight: 16 }}
        onPress={handleEditBusiness}
        activeOpacity={0.7}
      >
        <Ionicons name="create-outline" size={26} color={theme.text} />
      </TouchableOpacity>
    ) : null;

    const headerRight = isEditingBusiness
      ? <View style={{ width: 44, height: 44 }} />
      : editIcon;

    return (
      <AppShell 
        title="Business Profile" 
        showBackButton={true} 
        onBackPress={() => {
          setShowBusinessDetails(false);
          setIsEditingBusiness(false); // Reset edit mode when going back
        }}
        rightActions={headerRight}
        scrollable={false}
      >
        <View style={{ flex: 1 }}>
          <BusinessDetails
            profile={profile}
            isOwnProfile={isOwnProfile}
            isEditing={isEditingBusiness}
            onEditPress={handleEditBusiness}
            onSavePress={handleSaveBusinessChanges}
            onCancelPress={handleCancelBusinessEdit}
            onDeletePress={handleDeleteBusinessDetails}
            onWriteReview={!isOwnProfile ? handleWriteReview : undefined}
            isSaving={isSavingBusiness}
          />
        </View>
      </AppShell>
    );
  }

  return (
    <AppShell 
      title={isOwnProfile ? 'Profile' : profile.name}
      showBackButton={true}
      onBackPress={() => navigation.goBack()}
    >
      <ScrollView 
        key={refreshKey} 
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: 20 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
      >
        {/* Business Card */}
        <View style={styles.businessCardWrapper}>
          <BusinessCard
            userData={profile}
            onPress={() => {}} // Disable card press on profile screen
            footerContent={
              !isOwnProfile ? (
                <View style={styles.cardFooterButtons}>
                  {connectionStatus === 'connected' ? (
                    // Only show message button for connected users
                    <TouchableOpacity
                      style={[styles.messageButton, styles.messageButtonFull, { backgroundColor: theme.primary }]}
                      onPress={handleMessage}
                      activeOpacity={0.85}
                    >
                      <Ionicons name="chatbubble-outline" size={18} color="#000000" />
                      <ThemedText style={[styles.messageButtonText, { color: '#000000', marginLeft: 8 }]}>Message</ThemedText>
                    </TouchableOpacity>
                  ) : (
                    // Show connect button for non-connected users
                    renderConnectionButton()
                  )}
                </View>
              ) : undefined
            }
          />
        </View>

        {/* Detail Cards */}
        <View style={styles.detailCards}>
          <TouchableOpacity
            style={[styles.detailCard, { backgroundColor: theme.card }]}
            onPress={() => setShowPersonalDetails(true)}
            activeOpacity={0.7}
          >
            <View style={[styles.detailCardIcon, { backgroundColor: theme.primary + '20' }]}>
              <Ionicons name="person-outline" size={22} color={theme.primary} />
            </View>
            <View style={styles.detailCardContent}>
              <ThemedText style={[styles.detailCardTitle, { color: theme.text }]}>Personal Profile</ThemedText>
              <ThemedText style={[styles.detailCardSubtitle, { color: theme.textSecondary }]}>
                View personal information
              </ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.detailCard, { backgroundColor: theme.card }]}
            onPress={() => {
              // Only allow navigation if own profile OR has business profile
              if (isOwnProfile || hasBusinessProfile) {
                setShowBusinessDetails(true);
              }
            }}
            activeOpacity={(!isOwnProfile && !hasBusinessProfile) ? 1 : 0.7}
            disabled={!isOwnProfile && !hasBusinessProfile}
          >
            <View style={[styles.detailCardIcon, { backgroundColor: theme.primary + '20' }]}>
              <Ionicons name="briefcase-outline" size={22} color={theme.primary} />
            </View>
            <View style={styles.detailCardContent}>
              <ThemedText style={[styles.detailCardTitle, { color: theme.text }]}>Business Profile</ThemedText>
              <ThemedText style={[styles.detailCardSubtitle, { color: theme.textSecondary }]}>
                {hasBusinessProfile 
                  ? 'View business information' 
                  : isOwnProfile 
                    ? 'No business added' 
                    : 'No business profile'}
              </ThemedText>
            </View>
            {(isOwnProfile || hasBusinessProfile) && (
              <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.detailCard, { backgroundColor: theme.card }]}
            onPress={() => navigation.navigate('MyProducts', { userId: isOwnProfile ? undefined : userId })}
            activeOpacity={0.7}
          >
            <View style={[styles.detailCardIcon, { backgroundColor: theme.primary + '20' }]}>
              <Ionicons name="cube-outline" size={22} color={theme.primary} />
            </View>
            <View style={styles.detailCardContent}>
              <ThemedText style={[styles.detailCardTitle, { color: theme.text }]}>
                {isOwnProfile ? 'My Products' : 'Products'}
              </ThemedText>
              <ThemedText style={[styles.detailCardSubtitle, { color: theme.textSecondary }]}>
                {isOwnProfile ? 'Manage your products and services' : 'View products and services'}
              </ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.detailCard, { backgroundColor: theme.card }]}
            onPress={() => navigation.navigate('SocialLinks', { userId: isOwnProfile ? undefined : userId })}
            activeOpacity={0.7}
          >
            <View style={[styles.detailCardIcon, { backgroundColor: theme.primary + '20' }]}>
              <Ionicons name="share-social-outline" size={22} color={theme.primary} />
            </View>
            <View style={styles.detailCardContent}>
              <ThemedText style={[styles.detailCardTitle, { color: theme.text }]}>
                Social Links
              </ThemedText>
              <ThemedText style={[styles.detailCardSubtitle, { color: theme.textSecondary }]}>
                {isOwnProfile ? 'Manage your social media profiles' : 'View social media profiles'}
              </ThemedText>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
          </TouchableOpacity>
        </View>
      </ScrollView>

      <EditProfileModal
        visible={editProfileVisible}
        profile={profile}
        onClose={() => setEditProfileVisible(false)}
        onSave={handleSaveProfile}
      />

      <EditBusinessModal
        visible={editBusinessVisible}
        business={business}
        onClose={() => setEditBusinessVisible(false)}
        onSave={business ? handleUpdateBusiness : handleCreateBusiness}
      />

      <ConnectionTypeModal
        visible={showConnectionTypeModal}
        userName={profile?.name || 'this person'}
        mode="send"
        onSelect={handleConnectionTypeSelect}
        onCancel={handleConnectionTypeCancel}
      />
    </AppShell>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    // paddingBottom is now dynamic - applied inline with insets
  },
  businessCardWrapper: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  businessCardContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 24,
  },
  businessCard: {
    borderRadius: 32,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.4,
    shadowRadius: 24,
    elevation: 18,
    overflow: 'hidden',
    position: 'relative',
  },
  cardDecorLayer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  glowOrb: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: 110,
    backgroundColor: 'rgba(255,255,255,0.08)',
    opacity: 0.65,
  },
  glowOrbTop: {
    top: -60,
    right: -80,
  },
  glowOrbBottom: {
    bottom: -90,
    left: -40,
  },
  cardHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  cardAvatar: {
    marginRight: 18,
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 48,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  initials: {
    fontSize: 32,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    color: '#000000',
  },
  shareButton: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
    marginLeft: 12,
  },
  cardTitleBlock: {
    flex: 1,
  },
  cardName: {
    fontSize: 28,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    color: '#FFFFFF',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.25)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 6,
  },
  cardProfession: {
    fontSize: 16,
    fontWeight: '500',
    fontFamily: 'ComicNeue_400Regular',
    color: 'rgba(255,255,255,0.9)',
  },
  industryPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.25)',
    marginTop: 10,
  },
  industryPillText: {
    marginLeft: 6,
    fontSize: 12,
    fontFamily: 'ComicNeue_400Regular',
    color: 'rgba(255,255,255,0.9)',
  },
  cardDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.15)',
    marginVertical: 18,
  },
  businessBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.25)',
    borderRadius: 20,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    marginBottom: 18,
  },
  businessBadgeIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
    overflow: 'hidden',
  },
  businessLogoImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  businessBadgeText: {
    flex: 1,
  },
  businessBadgeTitle: {
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    color: '#FFFFFF',
    marginBottom: 2,
  },
  businessBadgeSubtitle: {
    fontSize: 13,
    fontFamily: 'ComicNeue_400Regular',
    color: 'rgba(255,255,255,0.85)',
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  infoTile: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.22)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    marginBottom: 10,
  },
  infoIconBadge: {
    width: 28,
    height: 28,
    borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  infoValue: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '600',
    fontFamily: 'ComicNeue_700Bold',
    color: '#FFFFFF',
  },
  cardFooterButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  messageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 4,
  },
  messageButtonFull: {
    flex: 1,
  },
  messageButtonText: {
    fontSize: 14,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    backgroundColor: 'rgba(0,0,0,0.25)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 4,
  },
  actionButtonText: {
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    marginLeft: 8,
  },
  detailCards: {
    paddingHorizontal: 16,
    gap: 10,
  },
  detailCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  detailCardIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  detailCardContent: {
    flex: 1,
  },
  detailCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'ComicNeue_700Bold',
    marginBottom: 2,
  },
  detailCardSubtitle: {
    fontSize: 12,
    fontFamily: 'ComicNeue_400Regular',
  },
  detailsContainer: {
    flex: 1,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 16,
    marginVertical: 16,
    borderRadius: 12,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    fontFamily: 'ComicNeue_700Bold',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 16,
    fontFamily: 'ComicNeue_400Regular',
    marginTop: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: 'ComicNeue_400Regular',
    marginTop: 12,
  },
});

export default ProfileScreen;
